import { useState, useEffect } from 'react'
import { api, type EmailInboxDto } from '@/api/client'

export default function EmailInboxSection() {
  const [inbox, setInbox] = useState<EmailInboxDto | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const [regenerating, setRegenerating] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [isFreeTier, setIsFreeTier] = useState(false)

  useEffect(() => {
    loadInbox()
  }, [])

  async function loadInbox() {
    try {
      setLoading(true)
      setError(null)
      const result = await api.getEmailInbox()
      if (result && 'emailAddress' in result) {
        setInbox(result as EmailInboxDto)
        setIsFreeTier(false)
      } else {
        setIsFreeTier(true)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load inbox')
    } finally {
      setLoading(false)
    }
  }

  async function handleCopy() {
    if (!inbox) return
    try {
      await navigator.clipboard.writeText(inbox.emailAddress)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Fallback for browsers without clipboard API
      const input = document.createElement('input')
      input.value = inbox.emailAddress
      document.body.appendChild(input)
      input.select()
      document.execCommand('copy')
      document.body.removeChild(input)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  async function handleRegenerate() {
    try {
      setRegenerating(true)
      setError(null)
      const result = await api.regenerateEmailInbox()
      if (result) {
        setInbox(result)
      }
      setShowConfirm(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to regenerate address')
    } finally {
      setRegenerating(false)
    }
  }

  if (loading) {
    return (
      <div className="card mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Email Inbox</h2>
        <div className="animate-pulse space-y-3">
          <div className="h-4 bg-gray-200 rounded w-3/4"></div>
          <div className="h-10 bg-gray-200 rounded"></div>
        </div>
      </div>
    )
  }

  if (isFreeTier) {
    return (
      <div className="card mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Email Inbox</h2>
        <p className="text-gray-600 mb-3">
          Forward receipt emails to your personal inbox address and we'll automatically create draft expenses with attached documents.
        </p>
        <div className="bg-[#F7FAFC] border border-[#E2E8F0] rounded-lg p-4">
          <p className="text-sm text-[#4A5568]">
            Upgrade to <span className="font-semibold text-[#1B365D]">Core</span> or <span className="font-semibold text-[#1B365D]">Pro</span> to get your personal email inbox address.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="card mb-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-4">Email Inbox</h2>
      <p className="text-gray-600 mb-4">
        Forward receipt emails to this address to automatically create draft expenses.
      </p>

      {error && (
        <div className="bg-red-50 text-red-600 rounded-lg p-3 mb-4 text-sm">
          {error}
        </div>
      )}

      {inbox && (
        <>
          {/* Email address display */}
          <div className="flex items-center gap-2 mb-4">
            <div className="flex-1 bg-[#F7FAFC] border border-[#E2E8F0] rounded-lg px-4 py-3 font-mono text-sm text-[#1B365D] select-all">
              {inbox.emailAddress}
            </div>
            <button
              onClick={handleCopy}
              className="px-4 py-3 bg-[#1B365D] text-white rounded-lg hover:bg-[#152a4a] transition-colors text-sm font-medium whitespace-nowrap"
            >
              {copied ? 'Copied!' : 'Copy'}
            </button>
          </div>

          {/* Regenerate section */}
          {!showConfirm ? (
            <button
              onClick={() => setShowConfirm(true)}
              className="text-sm text-[#4A5568] hover:text-[#1B365D] transition-colors underline"
            >
              Regenerate address
            </button>
          ) : (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-sm text-amber-800 mb-3">
                Regenerating creates a new address. Your old address will <strong>immediately stop working</strong>. Any forwarding rules you've set up will need to be updated.
              </p>
              <div className="flex gap-2">
                <button
                  onClick={handleRegenerate}
                  disabled={regenerating}
                  className="px-3 py-1.5 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors text-sm font-medium disabled:opacity-50"
                >
                  {regenerating ? 'Regenerating...' : 'Yes, regenerate'}
                </button>
                <button
                  onClick={() => setShowConfirm(false)}
                  className="px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}
